import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy } from 'lucide-react';
import Container from './Container';
import Logo from './Logo';
import { fadeUp, stagger, viewportOnce } from '@/lib/motion';
import { contact, nav, site } from '@/lib/content';
import { cn } from '@/lib/cn';

function CopyField({ label, value, href }: { label: string; value: string; href: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // no-op — clipboard unavailable
    }
  };

  return (
    <div className="group flex flex-col">
      <span className="eyebrow text-ink/40">{label}</span>
      <div className="mt-2 flex items-center gap-3">
        <a
          href={href}
          className="link-underline font-display text-2xl tracking-tight text-ink md:text-3xl"
        >
          {value}
        </a>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy ${label.toLowerCase()}`}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/10 text-ink/50 opacity-0 transition-all group-hover:opacity-100 hover:border-ink/30 hover:text-ink focus-visible:opacity-100',
            copied && 'border-copper/40 text-copper opacity-100',
          )}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function Contact() {
  const year = new Date().getFullYear();

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="border-t border-ink/10 bg-paper pt-24 md:pt-36"
    >
      <Container>
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 gap-16 md:grid-cols-[1.3fr_1fr] md:gap-20"
        >
          <div>
            <motion.div variants={fadeUp} className="eyebrow mb-6">
              Contact
            </motion.div>
            <motion.h2
              variants={fadeUp}
              id="contact-heading"
              className="text-balance font-display text-4xl leading-[1.05] tracking-tightest md:text-[3.5rem] lg:text-[4rem]"
            >
              {contact.closer}
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-6 text-base text-ink/60">
              {contact.tagline}
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp}
            className="flex flex-col gap-10 md:items-end md:pt-2"
          >
            <div className="w-full md:max-w-sm">
              <CopyField
                label="Email"
                value={contact.email}
                href={`mailto:${contact.email}`}
              />
            </div>
            <div className="w-full md:max-w-sm">
              <CopyField
                label="Phone"
                value={contact.phone}
                href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
              />
            </div>
            <div className="w-full md:max-w-sm">
              <span className="eyebrow text-ink/40">Offices</span>
              <p className="mt-2 font-display text-2xl tracking-tight md:text-3xl">
                {contact.cities}
              </p>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-24 flex flex-col items-start justify-between gap-8 border-t border-ink/10 py-10 text-sm text-ink/50 md:mt-32 md:flex-row md:items-center"
        >
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden h-4 w-px bg-ink/15 md:block" />
            <span>
              © {year} {site.name}. All rights reserved.
            </span>
          </div>
          <nav aria-label="Footer" className="flex flex-wrap gap-6">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="link-underline transition-colors hover:text-ink"
              >
                {item.label}
              </a>
            ))}
          </nav>
        </motion.div>
      </Container>
    </section>
  );
}
